import {
	AfterViewInit,
	Directive,
	EventEmitter,
	HostBinding,
	Input,
	NgZone,
	OnChanges,
	OnDestroy,
	Output,
	SimpleChanges,
	ElementRef
} from '@angular/core';
import { Observable, Subscription, filter } from 'rxjs';
import { ScrollDispatcher, CdkScrollable } from '@angular/cdk/scrolling';

import { PaginatedResult } from '../models/paginated-result';
import { CustomLocation } from '../services/location.custom';

type ScrollableData<T> = T[] | PaginatedResult<T>;

interface MoreDataNeededEvent<T> {
	index: number;
	limit: number;
	provideData(obs: Observable<ScrollableData<T>>): void;
}

interface ScrollState<T> {
	scrollTop: number;
	list: T[];
	count: number | undefined;
	limit: number;
	endReached: boolean;
	loading: boolean;
}

const STORE_NAME = 'copoVirtualScroller';

@Directive({
	selector: '[copoVirtualScroller]',
	exportAs: 'copoVirtualScroller',
})
export class VirtualScrollerDirective<T> implements OnDestroy, AfterViewInit, OnChanges {
	@HostBinding('class.copo-scroller') readonly block = true;
	@Input() dataId!: string;
	@Input() scrollOnChange: boolean | undefined;
	@Input() modelMapper: ((model: T) => T) | undefined;

	@Output() readonly moreDataNeeded = new EventEmitter<MoreDataNeededEvent<T>>();
	@Output() readonly dataRetrieved = new EventEmitter<ScrollableData<T>>();
	stateId: number;
	storeName = STORE_NAME;

	readonly list: T[] = [];
	count: number | undefined;

	scrollTop = 0;

	limit = 50;
	endReached = false;
	loading = false;
	errorOccured = false;
	listSubscription?: Subscription;
	ancestorScrollContainer?: CdkScrollable;

	private scrollSub?: Subscription;

	constructor(
		private readonly scrollService: ScrollDispatcher,
		private readonly ngZone: NgZone,
		private readonly el: ElementRef
	) {
		this.stateId = CustomLocation.GET_CURRENT_STATE_ID();
	}

	getState() {
		const state = <ScrollState<T> | null>CustomLocation.GET_STATE(this.storeName, this.stateId);
		if (state && this.modelMapper) {
			state.list = state.list.map(this.modelMapper);
		}
		return state;
	}

	setup() {
		this.storeName = `${ STORE_NAME }-${ this.dataId }`;
		const state = this.getState();
		if (!state) {
			this.list.splice(0, this.list.length);
			this.count = undefined;
			this.scrollTop = 0;
			this.limit = 50;
			this.endReached = false;
			this.loading = false;
			this.errorOccured = false;
			this.loadMoreData();
			return;
		}
		this.list.splice(0, this.list.length, ...state.list);
		this.count = state.count;
		this.limit = state.limit;
		this.endReached = state.endReached;
		if (!state.loading) {
			return;
		}
		this.loadMoreData();
	}

	subscribeToList(listObservable: Observable<ScrollableData<T>>) {
		if (this.listSubscription) {
			this.listSubscription.unsubscribe();
		}
		this.loading = true;
		this.errorOccured = false;
		this.listSubscription = listObservable.subscribe({
			next: res => {
				this.dataRetrieved.emit(res);
				let list;
				if (Array.isArray(res)) {
					list = res;
				} else {
					list = res.results;
					this.count = res.count;
				}
				if (this.modelMapper) {
					list = list.map(this.modelMapper);
				}
				this.list.splice(this.list.length, this.limit, ...list);

				if (list.length < this.limit) {
					this.endReached = true;
				}
				this.listSubscription = undefined;
				this.loading = false;
			},
			error: (err: unknown) => {
				this.errorOccured = true;
				this.listSubscription = undefined;
				this.loading = false;
				console.error(err);
			}
		});
	}

	ngOnChanges(changes: SimpleChanges) {
		if (!('dataId' in changes)) {
			return;
		}
		if (this.scrollOnChange === undefined || this.scrollOnChange) {
			this.scrollTop = 0;
		}
		this.saveState();
		if (this.loading && this.listSubscription) {
			this.listSubscription.unsubscribe();
			this.listSubscription = undefined;
			this.loading = false;
		}
		this.restoreScrollState();
		this.setup();
	}

	loadMoreData() {
		if (this.loading || this.endReached) {
			return;
		}
		this.moreDataNeeded.emit({
			index: this.list.length,
			limit: this.limit,
			provideData: obs => {
				this.subscribeToList(obs);
			}
		});
	}

	ngAfterViewInit() {
		const ancestorScrollContainers = this.scrollService.getAncestorScrollContainers(this.el);
		if (ancestorScrollContainers.length === 0) {
			throw new Error('there is no scroll container');
		}
		const ancestorScrollContainer =
			this.ancestorScrollContainer = ancestorScrollContainers[ancestorScrollContainers.length - 1];
		this.scrollSub = this.scrollService.scrolled()
			.pipe(filter(target => ancestorScrollContainer === target))
			.subscribe(event => {
				if (!event) {
					return;
				}
				this.scrollTop = event.measureScrollOffset('top');
				const parentEl = event.getElementRef();
				if (this.scrollTop + (parentEl.nativeElement.clientHeight * 10) < parentEl.nativeElement.scrollHeight) {
					return;
				}
				this.ngZone.run(() => {
					this.loadMoreData();
				});
			});
		this.restoreScrollState();
	}

	restoreScrollState() {
		const state = this.getState();
		if (state && this.ancestorScrollContainer) {
			this.scrollTop = state.scrollTop;
			this.ancestorScrollContainer.scrollTo({ top: state.scrollTop });
		}
	}

	ngOnDestroy() {
		if (this.listSubscription) {
			this.listSubscription.unsubscribe();
		}
		if (this.scrollSub) {
			this.scrollSub.unsubscribe();
		}
		this.saveState();
	}

	saveState() {
		const state: ScrollState<T> = {
			scrollTop: this.scrollTop,
			list: this.list,
			count: this.count,
			limit: this.limit,
			endReached: this.endReached,
			loading: this.errorOccured ? true : this.loading
		};
		CustomLocation.SET_STATE(this.storeName, this.stateId, state);
	}
}
